import { Localizer } from "@/main/react/Types";
import { toast } from "sonner";
import logger from "@/lib/logger";
import {LocalizationKey} from "@/main/util/LocalizationUtil";
import { ClientFactory } from "@/main/common/ClientFactory";
import { ShareInitiatedRequest } from "@/main/common/ShareTypes";
import { shareViaNativeShell } from "@/main/mobile-shell/MobileShareBridge";
import { openOutboundUrl } from "@/main/mobile-shell/MobileOutboundLinkBridge";
import { buildChannelUrl, ShareChannel } from "@/main/util/shareChannels";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
// The share beacon's `from_page`. Shared with the unusable-page probe's `route` dimension so the two
// normalize identically and stay joinable — see the module's own comment for why it is not local here.
import { currentRouteTemplate } from "@/lib/routeTemplate";

// The two cheap probes and their types live in `platformProbes.ts` (no imports) so the eager
// `androidAppOffer` predicate can read them without this module's 33 KB (FI-058, 2026-09-15). Re-exported
// and delegated here so every existing call site, and every spy on these statics, keeps working.
import {
    Platform,
    type EmbeddedBrowserFamily,
    getPlatform as probePlatform,
    getEmbeddedInAppBrowserFamily as probeEmbeddedInAppBrowserFamily,
    isEmbeddedInAppBrowser as probeIsEmbeddedInAppBrowser,
} from "@/main/util/platformProbes";

export { Platform, type EmbeddedBrowserFamily };

export class PlatformIdentifierUtil {
    static getPlatform(): Platform {
        return probePlatform();
    }

    static getEmbeddedInAppBrowserFamily(userAgentOverride?: string): EmbeddedBrowserFamily | null {
        return probeEmbeddedInAppBrowserFamily(userAgentOverride);
    }

    static isEmbeddedInAppBrowser(userAgentOverride?: string): boolean {
        return probeIsEmbeddedInAppBrowser(userAgentOverride);
    }

    static translateShortcutText(text: string): string {
        const platform = this.getPlatform();
        let result = text;
        if (platform === Platform.MacOS) {
            // replace occurrences of "ctrl" with "cmd"
            result = result.replace(/\bctrl\b/gi, "cmd");
        }
        if (platform === Platform.Windows || platform === Platform.Linux) {
            // replace occurrences of "cmd" with "ctrl"
            result = result.replace(/\bcmd\b/gi, "ctrl");
        }
        return result;
    }
}



/**
 * What is being shared, so the recorded SHARE_INITIATED row is attributable to a specific piece of
 * content. Optional: a share with no context still mints a token and still records, it just cannot say
 * which entity it was.
 */
export type ShareEntityType = "DISCUSSION" | "THOUGHT" | "GROUP" | "SERIES" | "USER";

/**
 * Which peak-satisfaction moment offered this share, when one did. Absent for the ambient share button.
 *
 * This is what makes a moment share distinguishable in analysis at all: `from_page` is route-normalized
 * to `/discussion/*`, which is the SAME value for a moment share and for the ambient
 * `discussion-share-button` on the very same page. Without this field the ambient button cannot serve as
 * the within-product control group and the moments can only be measured pre/post across a deploy.
 *
 * ⚠️ **This is the ARMED-DIALOG kind, and it is narrower than what the beacon accepts — do not widen it
 * to add a beacon tag.** Every value here owes three other things: a `?sv_m=` arming parameter
 * (`shareMomentParam.ts`), a once-per-`(kind, entityId)` entry in `shareMomentLedger.ts`, and a body
 * string in `ShareMomentDialog`'s `BODY_KEY` map. Widening it for a value that has none of those breaks
 * all three at compile time, which is the type system doing its job. Use {@link ShareMomentTag} for a
 * label that only ever reaches the beacon.
 */
export type ShareMomentKind =
    | "post_publish"
    | "first_response"
    | "first_post"
    // The three PRIDE moments (referral design §4 item 3, round-26 D5 / A4). Each maps to a notification
    // that already fires exactly once — `ANSWER_ACCEPTED`, `TOP_REPLY`, `DISCUSSION_GAINING_TRACTION` —
    // so the ledger's "once per kind × entity" and the notification's own once-ness agree by
    // construction rather than by a second threshold. `answer_accepted` and `top_reply` are addressed to
    // the THOUGHT's author, not the post's (A5); `gaining_traction` keeps the discussion-author gate.
    | "answer_accepted"
    | "top_reply"
    | "gaining_traction";

/**
 * Every value the `moment` BEACON may carry — a superset of {@link ShareMomentKind}.
 *
 * The two are deliberately not the same type. `ShareMomentKind` is the **armed-dialog** kind: each of
 * its values owes a `?sv_m=` arming param (`shareMomentParam.KNOWN_KINDS`), a `ShareMomentDialog`
 * `BODY_KEY` entry, and a dialog that opens on arrival. The extra tags here owe none of those — nothing
 * arms them, nothing re-opens on a later page load, and the user clicked a button already on screen.
 * They exist only so the beacon can tell these shares apart from the ambient one.
 *
 * `unresolved_person_search` is the search-results "couldn't find who you wanted" affordance.
 * `group_invite` (referral design §5A) is the group page's "invite people with a link" item — it sits in
 * the SAME menu as the ambient group Share and calls this exact function with the same `entityType` and
 * the same `/group/*` `from_page`, so without a tag the two are one indistinguishable row.
 *
 * Widening `ShareMomentKind` instead would be wrong and the build says so: `BODY_KEY` is a
 * `Record<ShareMomentKind, LocalizationKey>`, so a new kind demands dialog copy that has no dialog to
 * render in. That failure is the type system working, not an obstacle to route around.
 *
 * Widening THIS union REQUIRES widening `AjaxShareRestController.ALLOWED_MOMENTS` and its
 * `everyShippedMomentIsAllowlisted` parity test in the same task — see the warning on `ShareTypes.ts`.
 */
export type ShareMomentTag = ShareMomentKind | "unresolved_person_search" | "group_invite";

export interface ShareContext {
    entityType: ShareEntityType;
    entityId: string;
    /**
     * The content's own title (post title, group name, series title, display name) — NOT a UI button
     * label. Falls back to `shareUrl`'s positional `title` argument when absent, so a caller that has not
     * been threaded yet behaves exactly as it did before.
     */
    entityTitle?: string;
    /**
     * Display name of the author, supplied ONLY where the author actually authored the titled thing.
     * A THOUGHT deliberately does not pass one: its `entityTitle` is the *host post's* title, so naming
     * the replier there would attribute someone else's post to them.
     */
    authorDisplayName?: string;
    /**
     * The post being shared is a QUESTION (round-26 D6 / A6). Fed from
     * `DiscussionPageDataResponse.isQuestion`, the same projection the composer placeholder and the
     * empty states already read, so there is one source for "this post asks something".
     *
     * <p>It changes the recipient-facing SENTENCE and nothing else: no second share control is added to
     * the surface (rule 23). A question's share asks for the reader's take rather than announcing the
     * post, which is the one thing a question can say that an ordinary post cannot.
     */
    isQuestion?: boolean;
    /** Set by a share moment, a moment-shaped affordance, or a deliberate invite action; absent for the ambient share button. */
    moment?: ShareMomentTag;
}

/**
 * Caps on the two recipient-facing payload fields.
 *
 * 200 for `text` is not "whatever fits" — the same composed string is reused verbatim by the X intent in
 * the desktop drawer, where the budget is 280 characters including a 23-character `t.co` URL.
 * `200 + 1 + 23 = 224` leaves ~56 characters of headroom for a handle or prefix without needing a second,
 * divergent truncation rule. One cap, two consumers, no drift.
 *
 * Truncation happens AFTER interpolation because es/pt run ~20–25% longer than English; the English
 * source strings target ≤140 characters so the truncator never fires in normal use.
 */
const MAX_SHARE_TEXT_LENGTH = 200;
/** `title` is a subject line (Android `EXTRA_SUBJECT`). Hard truncate. */
const MAX_SHARE_TITLE_LENGTH = 100;

/** Query parameter carrying the share token. Mirrors backend `ShareToken.QUERY_PARAM`. */
const SHARE_TOKEN_PARAM = "sv_s";
const SHARE_TOKEN_LENGTH = 12;
const SHARE_TOKEN_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

/**
 * Mints an opaque share token: 12 random alphanumeric characters, ~4.7e18 possibilities.
 *
 * **Content-free by construction.** It travels in a public URL, so it encodes no user id, no username,
 * no timestamp and nothing else derivable — the token→sharer mapping lives server-side in the
 * SHARE_INITIATED activity row instead. That indirection is the whole point: a user id in a URL
 * parameter is not consent-gated and would persist for EEA visitors.
 *
 * Returns null when `crypto` is unavailable, in which case the share proceeds untokenized (still shared,
 * still recorded, just not attributable). `Math.random()` is deliberately NOT used as a fallback for a
 * value we treat as a join key. The modulo over a 36-character alphabet is very slightly biased, which is
 * irrelevant for an identifier that carries no security property.
 */
function generateShareToken(): string | null {
    const cryptoObject: Crypto | undefined =
        typeof globalThis !== "undefined" ? (globalThis.crypto as Crypto | undefined) : undefined;
    if (!cryptoObject || typeof cryptoObject.getRandomValues !== "function") {
        return null;
    }
    try {
        const bytes = new Uint8Array(SHARE_TOKEN_LENGTH);
        cryptoObject.getRandomValues(bytes);
        let token = "";
        for (let index = 0; index < bytes.length; index++) {
            token += SHARE_TOKEN_ALPHABET[bytes[index] % SHARE_TOKEN_ALPHABET.length];
        }
        return token;
    } catch {
        return null;
    }
}

/**
 * Appends the token to the URL being shared. Inserts **before any `#fragment`** — thought permalinks
 * carry one, and `...#thought-123?sv_s=x` would put the parameter inside the fragment where the server
 * never sees it. Plain string surgery rather than `new URL()`, so the shared link is not silently
 * renormalized (`https://x.com` → `https://x.com/`) in a value users paste in public.
 */
function withShareToken(url: string, token: string | null): string {
    if (!token || !url) {
        return url;
    }
    const hashIndex = url.indexOf("#");
    const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const fragment = hashIndex >= 0 ? url.slice(hashIndex) : "";
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${SHARE_TOKEN_PARAM}=${token}${fragment}`;
}

/** Hard truncate with an ellipsis. Used for `title`, which is a subject line and has no word budget. */
function hardTruncate(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
        return value;
    }
    return value.slice(0, maxLength - 1).trimEnd() + "…";
}

/**
 * Truncate on a word boundary, falling back to a hard cut when a single word exceeds the budget (a URL,
 * an unbroken hashtag, a language that does not space-separate).
 */
function truncateOnWordBoundary(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
        return value;
    }
    const clipped = value.slice(0, maxLength - 1);
    const lastSpace = clipped.lastIndexOf(" ");
    // Only honour the word boundary if it leaves a useful amount of text; otherwise cut hard.
    const body = lastSpace > maxLength * 0.6 ? clipped.slice(0, lastSpace) : clipped;
    return body.trimEnd() + "…";
}

/**
 * Removes any URL-bearing token from a composed share text.
 *
 * **Why this is not paranoia.** Several share targets concatenate `text` and `url` into one message
 * body, so a `text` that itself contains the link ships it TWICE into the same bubble — and WhatsApp then
 * unfurls only the first. The composed string comes from 25 translated catalogs plus caller-supplied
 * titles, and a translator pasting a URL into a value, or an author titling a post with one, are both
 * entirely plausible. The rule is absolute rather than conditional: the composed `text` never contains a
 * URL.
 */
function stripUrlTokens(value: string): string {
    return value
        .split(/\s+/)
        .filter((token) => !/https?:\/\//i.test(token) && !/\bwww\./i.test(token))
        .join(" ")
        .trim();
}

/**
 * Composes the `text` field of the native share payload from a localized template.
 *
 * Composition lives HERE, in the chokepoint, rather than at the five call sites: callers supply only the
 * facts they alone know (the title, the author) — exactly the split the attribution slice already
 * established for `{entityType, entityId}`. Building the string per caller would be five drift points,
 * and the sixth share affordance added later would get nothing.
 *
 * Returns `undefined` when there is nothing worth saying, in which case the payload keeps today's exact
 * `{title, url}` shape. Never returns `""` — an empty `text` still produces a leading blank line in some
 * Android targets.
 */
function buildShareText(localizer: Localizer, context?: ShareContext): string | undefined {
    const entityTitle = context?.entityTitle?.trim();
    if (!entityTitle) {
        return undefined;
    }
    const author = context?.authorDisplayName?.trim();
    // A question outranks the author line (D6). Naming the asker is what an announcement does; a
    // question's share is an invitation to answer, and the two sentences cannot both be said in 200
    // characters. Deliberately does NOT interpolate the author, so the one template serves every
    // question regardless of who asked.
    const composed = context?.isQuestion
        ? localizer("share.payload.question.text", entityTitle)
        : author
            ? localizer("share.payload.text", entityTitle, author)
            : localizer("share.payload.entity.text", entityTitle);

    const text = truncateOnWordBoundary(stripUrlTokens(composed), MAX_SHARE_TEXT_LENGTH);
    return text.length > 0 ? text : undefined;
}

/**
 * Records the share server-side so the token can later be resolved to whoever shared it.
 *
 * Strictly fire-and-forget: not awaited, every failure swallowed. A throttled or failing beacon must
 * never affect the share the user just performed — they have already got their link.
 */
function emitShareInitiated(
    surface: ShareInitiatedRequest["surface"],
    token: string | null,
    context?: ShareContext,
    channel?: ShareInitiatedRequest["channel"]
): void {
    try {
        void ClientFactory.recordShareInitiated({
            surface,
            channel,
            entityType: context?.entityType,
            entityId: context?.entityId,
            shareToken: token ?? undefined,
            fromPage: currentRouteTemplate() ?? undefined,
            // Omitted rather than sent as null when absent: `details_json ? 'moment'` is the predicate
            // that separates a moment share from an ambient one.
            moment: context?.moment,
        }).catch(() => {
            // Best-effort telemetry; the share already succeeded.
        });
    } catch {
        // Never let instrumentation surface to the caller.
    }
}

/**
 * shareUrl - a reusable sharing helper used across multiple button components.
 * - shareUrl: attempts native share when available, falls back to clipboard, then prompt.
 * - It will show localized toasts when a localizer and keys are provided. If not, it uses simple English defaults.
 *
 * This, {@link copyUrlToClipboard} and {@link performChannelShare} are the ONLY three share chokepoints in
 * the app, which is why share instrumentation lives here rather than in each button component: every share
 * surface is covered by construction, and a share affordance added later is instrumented without anyone
 * remembering to. (The desktop drawer added the third in 2026-07; it calls `performChannelShare` and never
 * mints a token or emits a beacon itself, so the mint → append → emit trio stays in this one file.)
 *
 * SHARE_INITIATED is emitted on **success only**. A dismissed native share sheet rejects with
 * `AbortError`, which stays a silent no-op — no event, no token, nothing. A dismissal is not a share, and
 * counting it would inflate the numerator of every share→visit rate. The Capacitor sheet rejects with a
 * plain Error rather than an `AbortError`, so its dismissal is translated to a value by
 * {@link shareViaNativeShell} before it can reach the catch block — see the branch below.
 *
 * The full order is: **native shell sheet → `navigator.share` → drawer → clipboard.** The first branch is
 * a capability check on `window.Capacitor.Plugins.Share`, so on the web and in Playwright it is a single
 * property read that changes nothing.
 */
export async function shareUrl(
    url: string,
    title: string,
    localizer: Localizer,
    context?: ShareContext,
    // Quote post (design §10.2). Supplied ONLY by DiscussionButtons, and only for a signed-in viewer:
    // the drawer has no viewer identity of its own, so "who may quote" is decided at the call site and
    // an anonymous viewer simply gets no row. Threaded as an explicit parameter rather than smuggled
    // into ShareContext, which is a DATA payload that also feeds analytics.
    onQuote?: () => void
): Promise<boolean> {
    const baseUrl = url ?? (typeof window !== 'undefined' ? window.location.href : '');
    // The CONTENT's title, not the UI button label. Every call site used to pass a localized "Share",
    // which on Android becomes Intent.EXTRA_SUBJECT — so sharing a poem to Gmail produced an email whose
    // subject line was the word "Share". The positional argument stays the fallback so an un-threaded
    // caller behaves exactly as before.
    const rawTitle = context?.entityTitle?.trim()
        || title
        || (typeof document !== 'undefined' ? document.title : '');
    const shareTitle = hardTruncate(rawTitle ?? '', MAX_SHARE_TITLE_LENGTH);
    const shareText = buildShareText(localizer, context);
    const shareToken = generateShareToken();
    const shareUrl = withShareToken(baseUrl, shareToken);

    try {
        // THE NATIVE SHELL'S OS SHEET, ahead of the `navigator.share` probe below — and still a
        // capability check, not a platform check. `shareViaNativeShell` gates on
        // `window.Capacitor.Plugins.Share` being present, so this costs one property read and returns
        // "unavailable" on the web, in Playwright, and inside any APK built before @capacitor/share
        // landed. Order is: native shell → navigator.share → drawer → clipboard. If a future WebView
        // ships Web Share, both branches are correct and this one wins, which is the outcome we want —
        // the Capacitor plugin is the one whose behaviour we can read the source of.
        //
        // `dialogTitle` is NOT cosmetic: SharePlugin.java:84 defaults it to the hardcoded English
        // string "Share", so omitting it ships an English chooser heading to 24 locales. `literal.share`
        // already exists in every catalog (it is a call-site title in UserButtons/GroupButtons), so
        // this costs no new keys.
        //
        // ⚠️ The outcome is a DISCRIMINATED value, never a thrown error, and that is the point. A
        // dismissed Android chooser rejects with an ordinary Error whose name is NOT "AbortError", so a
        // naive wire-up would fall into the catch below and show `share_url.share_failed` to every user
        // who simply backed out. `dismissed` therefore returns false silently, exactly like AbortError.
        const nativeShellDialogTitle = localizer("literal.share");
        const nativeShellOutcome = await shareViaNativeShell(shareText
            ? { title: shareTitle, text: shareText, url: shareUrl, dialogTitle: nativeShellDialogTitle }
            : { title: shareTitle, url: shareUrl, dialogTitle: nativeShellDialogTitle });
        if (nativeShellOutcome === "shared") {
            emitShareInitiated("native", shareToken, context, "native_sheet");
            return true;
        }
        if (nativeShellOutcome === "dismissed") {
            return false;
        }
        // "unavailable" falls through to exactly today's chain, untouched.

        if (navigator && (navigator as any).share) {
            // Never send `text: ""` — an empty value still produces a leading blank line in some Android
            // targets. With no context the payload shape is byte-identical to what it has always been.
            await (navigator as any).share(shareText
                ? { title: shareTitle, text: shareText, url: shareUrl }
                : { title: shareTitle, url: shareUrl });
            emitShareInitiated("native", shareToken, context, "native_sheet");
            return true;
        }

        // No native sheet: offer the drawer before silently copying.
        //
        // Detection is EXACTLY `typeof navigator.share === "function"` above — no user-agent sniffing
        // (`getPlatform()` exists and is the wrong tool: the question is capability, and a UA table goes
        // stale the release after it is written) and no `canShare()` (its documented purpose is validating
        // `files` support and it shipped LATER than `share()` in both Safari and Chrome, so gating a
        // text-only payload on it could only SUBTRACT user agents that support `share({title,text,url})`
        // perfectly well).
        //
        // A useful side effect: headless Chromium exposes no `navigator.share`, so the drawer branch is
        // the DEFAULT in Playwright and needs no stub at all — the exact inverse of the attribution spec's
        // problem, which had to ADD `navigator.share` because its absence silently redirected the test to
        // the clipboard branch.
        if (await tryOpenShareDrawer(baseUrl, shareTitle, shareText, localizer, context, onQuote)) {
            return true;
        }

        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            // The clipboard payload stays URL-ONLY, deliberately, and the symmetry with the sheet is
            // wrong for four independent reasons: (1) it steals the self-presentation act — the sharer is
            // about to type their own sentence into the recipient's composer and would have to delete
            // ours first, in a buffer they cannot see until it is already sent; (2) multi-line clipboard
            // content breaks single-line paste targets (URL bars, Slack's unfurl, the "paste a link"
            // pickers in Instagram/X); (3) unfurling already does this job from the bare URL via the OG
            // tags we emit server-side; (4) `clipboard` is the highest-volume series we have, so changing
            // the payload underneath it makes before/after non-comparable for no gain.
            await navigator.clipboard.writeText(shareUrl);
            const msg = localizer("share_url.copied_to_clipboard");
            try { toast.success(msg); } catch {}
            emitShareInitiated("clipboard", shareToken, context, "clipboard");
            return true;
        }
        // `DISCUSSION-58`, the same absence one chokepoint over. Reaching here means every route was
        // refused — no native shell, no `navigator.share`, the drawer would not open, and no async
        // clipboard API — so the member pressed Share and NOTHING happened, which is the one outcome a
        // share control must never produce silently. Same marker-plus-calm-toast treatment as the
        // drawer's clipboard channel; no new key.
        logger.error("[share] every share route was unavailable (no native shell, no navigator.share, no drawer, no clipboard)");
        try { toast.error(localizer("share_url.share_failed")); } catch {}
        return false;
    } catch (err) {
        // Dismissing the native share sheet rejects with an AbortError; that is a normal
        // user action, not a failure, so treat it as a silent no-op (no log, no toast, no event).
        if ((err as { name?: string })?.name === "AbortError") {
            return false;
        }
        // send to logger instead of console
        logger.error(err);
        const msg = localizer("share_url.share_failed");
        try { toast.error(msg); } catch {}
        return false;
    }
}

/**
 * Attempts to open the desktop fallback drawer. Returns false when it could not be opened, in which case
 * `shareUrl` continues to the clipboard branch.
 *
 * <p>⚠️ <strong>If the chunk fails to load, fall through to clipboard + toast — today's exact behavior.</strong>
 * A share button must never become a no-op because a chunk 404'd: JS may only improve on what the page
 * already did.
 */
async function tryOpenShareDrawer(
    url: string,
    title: string,
    text: string | undefined,
    localizer: Localizer,
    context?: ShareContext,
    onQuote?: () => void
): Promise<boolean> {
    if (typeof document === "undefined") {
        return false;
    }
    try {
        // Routed through the chunk-resilience helper, which also keeps `vaul` and the channel module off
        // the eager startup graph — a static import here would trip `check-bundle-budget.mjs`, so this
        // risk is loud rather than silent.
        const module = await importChunkWithRetryAndReload(
            () => import("@/main/react/share/openShareDrawer"),
            "share-drawer"
        );
        return module.default({ url, title, text, localizer, context, onQuote });
    } catch {
        return false;
    }
}

/**
 * Performs a share to ONE explicitly chosen channel. Called only by the drawer.
 *
 * <p><strong>One token per channel CLICK, not per drawer opening.</strong> The obvious alternative — mint
 * once when the drawer opens and reuse it — is cheaper and wrong: the token is the join key from a link in
 * the wild back to a `SHARE_INITIATED` row, so two rows sharing a token make the recipient's arrival join
 * 1:N and unattributable to a channel, which is the only new question the drawer exists to answer. A user
 * who clicks Copy and then WhatsApp therefore produces TWO rows and TWO distinct links. That is correct —
 * two links entered the world — and it is written down here so nobody later "deduplicates" it and destroys
 * channel attribution.
 *
 * <p><strong>Ordering matters for popup blockers.</strong> `window.open` loses transient user activation
 * across an `await`, and a blocked popup means the channel silently does nothing. So: mint (synchronous),
 * build, open, and only THEN fire the beacon and analytics, both fire-and-forget.
 */
export async function performChannelShare(
    channel: ShareChannel,
    url: string,
    localizer: Localizer,
    context?: ShareContext,
    text?: string
): Promise<boolean> {
    const shareToken = generateShareToken();
    const target = withShareToken(url, shareToken);

    if (channel === "clipboard") {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(target);
                try { toast.success(localizer("share_url.copied_to_clipboard")); } catch {}
                emitShareInitiated("drawer", shareToken, context, "clipboard");
                return true;
            }
            // `DISCUSSION-58`. An ABSENT async clipboard API used to be the one silent outcome in this
            // function: a THROWN error already got the calm failure toast, while a missing
            // `navigator.clipboard.writeText` returned false with no toast, no log and no state change —
            // `handleChannel` then did not even close the drawer, so the member tapped the sheet's
            // PRIMARY action and the screen did not move. The two cases are the same failure to the
            // member and now reach the same place.
            //
            // This is exactly the population it matters for: the drawer only opens where
            // `navigator.share` is missing, which on a phone means a WebView or an in-app browser — the
            // environments most likely to restrict the async clipboard API in the first place.
            //
            // The marker is a real log line rather than a bare `return false`, per the AGENTS.md rule on
            // catches that silently degrade a user-visible feature: if the only reason a member does not
            // see an error is that we handled it, the monitoring has to see it instead.
            logger.error("[share] clipboard channel unavailable: navigator.clipboard.writeText is missing");
            try { toast.error(localizer("share_url.share_failed")); } catch {}
        } catch (err) {
            logger.error(err);
            try { toast.error(localizer("share_url.share_failed")); } catch {}
        }
        return false;
    }

    const channelUrl = buildChannelUrl(channel, target, text);
    if (!channelUrl) {
        return false;
    }

    // Inside the native Android shell a `window.open` popup has nowhere to go: `setSupportMultipleWindows`
    // is never called and `BridgeWebChromeClient` does not override `onCreateWindow`, so a channel button
    // is at best a navigation that leaves the app and at worst a `null` return that tells the user
    // sharing FAILED — on a share that was never actually attempted. The bridge hands the channel URL to
    // a Chrome Custom Tab instead, which stays in the app's task so Back returns to Seavien. It is inert
    // outside the shell: `"declined"` means the ordinary `window.open` below runs exactly as it always
    // has, so web behaviour is byte-identical.
    //
    // Called synchronously, before the beacon, for the popup-blocker reason stated above: nothing may
    // `await` between the click and the open.
    // Design D9: designs/01-now/2026-07-30-native-shell-outbound-links-downloads-and-open-in-design.md.
    const nativeOutcome = openOutboundUrl(channelUrl);

    if (nativeOutcome === "blocked") {
        // The bridge refused the scheme. Unreachable for a built channel URL (they are all https), but
        // reporting a REFUSED share to the user as a successful one is exactly why `openOutboundUrl` is
        // three-valued rather than a boolean, so the branch is written rather than assumed away.
        try { toast.error(localizer("share_url.share_failed")); } catch {}
        return false;
    }

    if (nativeOutcome === "declined") {
        const opened = typeof window !== "undefined"
            ? window.open(channelUrl, "_blank", "noopener,noreferrer")
            : null;
        if (!opened) {
            // Popup blocked. Reuse the existing, calm, already-translated failure toast rather than minting a
            // new key for a residual case.
            try { toast.error(localizer("share_url.share_failed")); } catch {}
            return false;
        }
    }

    emitShareInitiated("drawer", shareToken, context, channel);
    return true;
}

/**
 * copyUrlToClipboard - copies a URL to the clipboard and shows a localized toast.
 * Unlike shareUrl, this always copies (never invokes the native share sheet), which is
 * what an explicit "Copy link" action should do.
 */
export async function copyUrlToClipboard(
    url: string,
    localizer: Localizer,
    context?: ShareContext
): Promise<boolean> {
    const baseUrl = url ?? (typeof window !== 'undefined' ? window.location.href : '');
    const shareToken = generateShareToken();
    const target = withShareToken(baseUrl, shareToken);

    try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(target);
            try { toast.success(localizer("share_url.copied_to_clipboard")); } catch {}
            emitShareInitiated("clipboard", shareToken, context, "clipboard");
            return true;
        }
        return false;
    } catch (err) {
        logger.error(err);
        try { toast.error(localizer("share_url.share_failed")); } catch {}
        return false;
    }
}


/**
 * FI-585 (round 37 `1b` A3): the canonical post URL plus a standard text fragment that browsers scroll to and
 * highlight natively. A selection of more than {@link TEXT_FRAGMENT_EDGE_WORDS} × 2 words is addressed as
 * `textStart,textEnd` (its first and last {@link TEXT_FRAGMENT_EDGE_WORDS} words), a shorter one whole. `-`, `,`
 * and `&` carry syntax inside a fragment directive, so they are escaped on top of `encodeURIComponent`.
 */
export const TEXT_FRAGMENT_EDGE_WORDS = 4;

function encodeTextFragmentPart(part: string): string {
    return encodeURIComponent(part).replace(/-/g, "%2D").replace(/,/g, "%2C").replace(/&/g, "%26");
}

export function buildTextFragmentUrl(baseUrl: string, selectionText: string): string {
    const words = selectionText.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
    const cleanBase = baseUrl.split("#")[0];
    if (words.length === 0) {
        return cleanBase;
    }
    const directive = words.length > TEXT_FRAGMENT_EDGE_WORDS * 2
        ? `${encodeTextFragmentPart(words.slice(0, TEXT_FRAGMENT_EDGE_WORDS).join(" "))},${encodeTextFragmentPart(words.slice(-TEXT_FRAGMENT_EDGE_WORDS).join(" "))}`
        : encodeTextFragmentPart(words.join(" "));
    return `${cleanBase}#:~:text=${directive}`;
}
